import {
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnInit,
  OnDestroy,
  SimpleChanges,
  OnChanges,
  Output,
  AfterViewInit,
  ViewChild,
  ElementRef,
  Renderer2,
  NgZone,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgSelectConfig } from '@ng-select/ng-select';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil, tap } from 'rxjs/operators';
import { DropdownFoundation } from '../dropdown-foundation';

@Component({
  selector: 'base-filter-dropdown',
  templateUrl: 'base-filter-dropdown.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BaseFilterDropdownComponent),
      multi: true,
    },
  ],
})
export class BaseFilterDropdownComponent
  extends DropdownFoundation
  implements ControlValueAccessor, OnChanges, OnInit, OnDestroy, AfterViewInit
{
  @ViewChild('filterSelect', { static: false }) filterSelectRef!: ElementRef;

  /**
   * Items to be handed into dropdown options
   */
  @Input() set items(items: any[]) {
    this._items = items;

    if (!this.virtualScroll) {
      this.itemsBuffer = this._items;
      return;
    }

    if (this._items?.length) {
      this.itemsBuffer = this._items.slice(0, this.itemsBufferSize);
    }
  }

  /**
   * Buffer for virtual scrolling of ng select. This becomes relevant when <br/>
   * "virtualScroll" property is true.
   * In this case this is the data added to the items property of ng-select.
   * Array of data will increase while scrolling.
   * @private
   */
  itemsBuffer: any[] = [];
  _items: any[] = [];
  searchSubject$ = new Subject<string>();
  /**
   * Binding label of dropdown items to a specific data structure<br/>
   * default = 'name'
   */
  @Input() bindOptionLabel: string = 'name';
  /**
   * Display or hide sub label text for dropdown option; <br/>
   * default = false
   */
  @Input() displaySubLabel: boolean = false;
  /**
   * optionSubLabelSegments to display sub label as custom field from object; <br/>
   * default = []
   */
  @Input() optionSubLabelSegments: string[] = [];
  /**
   * Filter name displayed in filter button
   */
  @Input() filterName: string;
  /**
   * Makes the search available<br/>
   * default = false
   */
  @Input() searchable: boolean = false;
  /**
   * Makes checkboxes available<br/>
   * default = true
   */
  @Input() multiple: boolean = true;
  /**
   * Adds class to label of dropdown-option based on item property <br/>
   * implementation: item[optionClass]
   */
  @Input() optionClass: string;
  /**
   * combines two labels of data model <br/>
   * replaces label of bindOptionLabel; never use both together <br/>
   * suitable for e.g. firstName and lastName <br/>
   * implementation: item[combineLabel[0]] + ' ' + item[combineLabel[1]]
   */
  @Input() combineLabel: [string, string];
  /**
   * Set this to true activates the ng-select virtual scroll
   * @link https://ng-select.github.io/ng-select#/virtual-scroll
   */
  @Input() virtualScroll = false;
  /**
   * Defines the search property.
   * This can be a string only (default) or any object property like "name" for example.
   * It is also possible to pass a comma separated string (without spaces). In this case
   * each string represents a property. Search will try to match those values in the
   * given order.
   */
  @Input() searchProperty: string = 'name';
  /**
   * Apply layout for displaying rating <br/>
   * default = false
   * ###Attention: is usable only with customLayout
   */
  @Input() isRatingLayout: boolean = false;
  /**
   * Apply custom icon instead of avatar image for custom layout <br/>
   * ###Attention: is usable only with customLayout
   */
  @Input() customAvatarIcon: string = '';
  /**
   * Debounced search term typed into the dropdown, for consumers that load matching
   * items from the server (typeahead). The client-side filtering of `items` still applies.
   */
  @Output() searchTermChange = new EventEmitter<string>();
  /**
   * The amount of items, that will be loaded when "virtualScroll" property is<br/>
   * set to true and scrolling reaches the zone of property "numberOfItemsFromEndBeforeFetchingMore".
   * @private
   */
  private itemsBufferSize = 30;
  /**
   * If property "virtualScroll" is set to true, this value represents for the<br/>
   * buffer zone items when additional data will be added to the list of items.
   * @private
   */
  private numberOfItemsFromEndBeforeFetchingMore = 10;
  /**
   * Loading status for virtual scrolling.
   * @private
   */
  private loadVirtualScrolling = false;

  private destroy$ = new Subject<void>();

  @HostBinding('class') class = 'filter-dropdown';

  @HostBinding('class.virtual-scroll')
  get isVirtualScroll(): boolean {
    return this.virtualScroll;
  }

  constructor(
    protected ngSelectConfig: NgSelectConfig,
    protected translate: TranslateService,
    private host: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private ngZone: NgZone
  ) {
    super(ngSelectConfig, translate);
  }

  get items() {
    return this._items;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.items?.currentValue) {
      if (!this.virtualScroll) {
        this.itemsBuffer = changes.items.currentValue;
        return;
      }

      if (changes?.items?.currentValue.length) {
        this.itemsBuffer = this.items.slice(0, this.itemsBufferSize);
      }
    }
  }

  ngOnInit() {
    if (!this.virtualScroll) {
      this.itemsBufferSize = 200;
    }

    this.searchHandler();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.adjustWidth();
    });

    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => this.adjustWidth());
      });
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onScroll(end: number) {
    if (this.virtualScroll || this.loadVirtualScrolling || this.items.length <= this.itemsBuffer.length) {
      return;
    }

    if (end + this.numberOfItemsFromEndBeforeFetchingMore >= this.itemsBuffer.length) {
      this.fetchMoreItems();
    }
  }

  fetchMoreItems(searchValue: string = '') {
    const fetchedItems = this.fetchMore(
      this._items,
      this.itemsBuffer,
      this.itemsBufferSize,
      this.searchProperty,
      searchValue
    );
    this.loadVirtualScrolling = true;
    setTimeout(() => {
      this.loadVirtualScrolling = false;
      this.itemsBuffer = this.itemsBuffer.concat(fetchedItems);
    }, 200);
  }

  private searchHandler() {
    this.searchSubject$
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(200),
        distinctUntilChanged(),
        tap((searchString) => this.searchTermChange.emit(searchString || '')),
        switchMap((searchString) =>
          this.searchService(this._items, searchString ? searchString : '', this.searchProperty)
        )
      )
      .subscribe((data) => {
        this.itemsBuffer = data.slice(0, this.itemsBufferSize);
      });
  }

  private adjustWidth() {
    const selectEl = this.host.nativeElement;
    const placeholderEl = selectEl.querySelector<HTMLElement>('.ng-placeholder');
    const inputEl = selectEl.querySelector<HTMLInputElement>('.ng-input input');

    if (!placeholderEl || !inputEl) {
      return;
    }

    const w = placeholderEl.getBoundingClientRect().width;
    this.renderer.setStyle(inputEl, 'width', `${w}px`);
  }
}
